import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../../api/http";
import {
  fetchStaffAvailability,
  patchStaffAvailability,
  type AvailabilityExceptionRow,
  type RecurringAvailabilityRow,
} from "../../../api/staffApi";
import { dayName } from "../staffFormat";
import { AvailabilityDayRow } from "../components/AvailabilityDayRow";
import { StaffAlert } from "../components/StaffAlert";
import { StaffAppShell } from "../components/StaffAppShell";
import { SkeletonCard } from "../components/SkeletonCard";

const DEFAULT_START = "09:00";
const DEFAULT_END = "17:00";

type DayState = { dayOfWeek: number; available: boolean; startTime: string; endTime: string };

function buildWeek(recurring: RecurringAvailabilityRow[]): DayState[] {
  return Array.from({ length: 7 }, (_, dayOfWeek) => {
    const row = recurring.find((r) => r.dayOfWeek === dayOfWeek);
    return {
      dayOfWeek,
      available: row?.available ?? false,
      startTime: row?.startTime?.slice(0, 5) ?? DEFAULT_START,
      endTime: row?.endTime?.slice(0, 5) ?? DEFAULT_END,
    };
  });
}

export function StaffAvailabilityPage() {
  const [week, setWeek] = useState<DayState[]>([]);
  const [exceptions, setExceptions] = useState<AvailabilityExceptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [exDate, setExDate] = useState("");
  const [exAvailable, setExAvailable] = useState(true);
  const [exStart, setExStart] = useState(DEFAULT_START);
  const [exEnd, setExEnd] = useState(DEFAULT_END);
  const [exNote, setExNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchStaffAvailability();
      setWeek(buildWeek(data.recurring));
      setExceptions(data.exceptions);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load availability.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(() => week.length > 0, [week]);

  const saveWeek = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await patchStaffAvailability({
        recurring: week.map((d) => ({
          dayOfWeek: d.dayOfWeek,
          available: d.available,
          startTime: d.available ? d.startTime : null,
          endTime: d.available ? d.endTime : null,
        })),
      });
      setWeek(buildWeek(data.recurring));
      setExceptions(data.exceptions);
      setSuccess("Weekly availability saved.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const addException = async () => {
    if (!exDate) {
      setError("Pick a date for the exception.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data = await patchStaffAvailability({
        exception: {
          exceptionDate: exDate,
          available: exAvailable,
          startTime: exAvailable ? exStart : null,
          endTime: exAvailable ? exEnd : null,
          note: exNote.trim() || null,
        },
      });
      setExceptions(data.exceptions);
      setExDate("");
      setExNote("");
      setSuccess("Exception saved.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save exception.");
    } finally {
      setSaving(false);
    }
  };

  const removeException = async (id: string) => {
    setSaving(true);
    try {
      const data = await patchStaffAvailability({ deleteExceptionId: id });
      setExceptions(data.exceptions);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not remove exception.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <StaffAppShell title="Availability" showBack backTo="/staff/me" hideNav>
      <div className="staff-page">
        {error ? <StaffAlert onDismiss={() => setError(null)}>{error}</StaffAlert> : null}
        {success ? (
          <StaffAlert variant="success" onDismiss={() => setSuccess(null)}>{success}</StaffAlert>
        ) : null}

        {loading ? (
          <SkeletonCard />
        ) : (
          <>
            <section className="staff-section">
              <h3 className="staff-section__title">Weekly schedule</h3>
              <div className="staff-avail-list">
                {week.map((day) => (
                  <AvailabilityDayRow
                    key={day.dayOfWeek}
                    dayLabel={dayName(day.dayOfWeek)}
                    available={day.available}
                    startTime={day.startTime}
                    endTime={day.endTime}
                    onChange={(patch) =>
                      setWeek((prev) =>
                        prev.map((d) => (d.dayOfWeek === day.dayOfWeek ? { ...d, ...patch } : d)),
                      )
                    }
                  />
                ))}
              </div>
              <button
                type="button"
                className="primary-button staff-full-btn"
                disabled={!dirty || saving}
                onClick={() => void saveWeek()}
              >
                {saving ? "Saving…" : "Save weekly availability"}
              </button>
            </section>

            <section className="staff-section">
              <h3 className="staff-section__title">Date exception</h3>
              <label className="staff-field">
                <span>Date</span>
                <input type="date" value={exDate} onChange={(e) => setExDate(e.target.value)} />
              </label>
              <label className="staff-avail-row__toggle">
                <input
                  type="checkbox"
                  checked={exAvailable}
                  onChange={(e) => setExAvailable(e.target.checked)}
                />
                <span>Available on this date</span>
              </label>
              {exAvailable ? (
                <div className="staff-avail-row__times">
                  <input type="time" value={exStart} onChange={(e) => setExStart(e.target.value)} />
                  <span className="muted">to</span>
                  <input type="time" value={exEnd} onChange={(e) => setExEnd(e.target.value)} />
                </div>
              ) : null}
              <label className="staff-field">
                <span>Note</span>
                <input type="text" value={exNote} onChange={(e) => setExNote(e.target.value)} placeholder="Optional" />
              </label>
              <button type="button" className="secondary-button staff-full-btn" disabled={saving} onClick={() => void addException()}>
                Add exception
              </button>

              {exceptions.length > 0 ? (
                <ul className="staff-exception-list">
                  {exceptions.map((ex) => (
                    <li key={ex.id}>
                      <div>
                        <strong>{ex.exceptionDate}</strong>
                        <span className="muted">
                          {ex.available ? `${ex.startTime ?? ""} – ${ex.endTime ?? ""}` : "Unavailable"}
                        </span>
                        {ex.note ? <p className="muted">{ex.note}</p> : null}
                      </div>
                      <button type="button" className="staff-text-button" onClick={() => void removeException(ex.id)}>
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          </>
        )}
      </div>
    </StaffAppShell>
  );
}
